import { useState, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Eye, EyeOff, BookOpen, ArrowRight } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { usePost } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { registerSchema, registerResponseSchema } from "../../utils/schemas/register-schema"
import { toastUtils } from "../../utils/toast"
import { tokenManager } from "../../utils/apiComponent/apiClient"
import { getEnrollmentIntent } from "../../utils/enrollmentIntent"
import { Badge } from "../../components/ui/badge"
import { useTranslation } from "../../i18n"
import PhoneInput from "../../components/PhoneInput"
import CountrySelector from "../../components/CountrySelector"

const API_URL = "https://api.centralapis.com/api/v1/country"

// Fonction helper pour faire des requêtes à l'API externe sans credentials
const fetchCountryByCode = async (countryCode) => {
  try {
    const response = await fetch(`${API_URL}?alpha2Code=${countryCode}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // Ne pas inclure credentials pour éviter les problèmes CORS
      credentials: 'omit',
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error fetching country:", error)
    throw error
  }
}

export default function Register() {
  const { tObj } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [enrollmentIntent, setEnrollmentIntent] = useState(null)
  const [phoneCountry, setPhoneCountry] = useState("CI")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [callingCode, setCallingCode] = useState("225") // Code par défaut pour CI
  
  // Vérifier si on a un contexte d'enrollment
  useEffect(() => {
    const enrollParam = searchParams.get('enroll')
    if (enrollParam) {
      // Récupérer l'intention d'enrollment depuis sessionStorage
      const intent = getEnrollmentIntent()
      if (intent) {
        setEnrollmentIntent(intent)
      }
    }
  }, [searchParams])

  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue, watch } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      address: "",
      city: "",
      country: "",
    }
  })

  // Charger le code d'appel quand le pays change
  useEffect(() => {
    const loadCallingCode = async () => {
      if (phoneCountry) {
        try {
          const data = await fetchCountryByCode(phoneCountry)
          if (data?.etat && data?.result?.[0]?.callingCodes?.[0]) {
            setCallingCode(data.result[0].callingCodes[0])
          }
        } catch (error) {
          console.error("Error getting calling code:", error)
          // En cas d'erreur, utiliser un code par défaut basé sur le pays
          if (phoneCountry === "CI") {
            setCallingCode("225")
          }
        }
      }
    }
    loadCallingCode()
  }, [phoneCountry])

  // Mettre à jour le champ phone quand le numéro ou le code change
  useEffect(() => {
    if (phoneNumber && callingCode) {
      setValue("phone", `+${callingCode}${phoneNumber}`)
    } else if (phoneNumber) {
      setValue("phone", phoneNumber)
    } else {
      setValue("phone", "")
    }
  }, [phoneNumber, callingCode, setValue])

  const { execute: registerExecute, loading: signupLoading } = usePost(endpoints.register, { 
    isPublicRoute: true,
    schema: registerResponseSchema 
  })

  const onSubmit = async (data) => {
    try {
      const payload = {
        name: data.name,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        ...(data.phone && { phone: data.phone }),
        ...(data.address && { address: data.address }),
        city: data.city,
        country: data.country,
      }

      const response = await registerExecute(payload)

      // Ne pas connecter l'utilisateur automatiquement
      // L'utilisateur doit d'abord vérifier son email
      
      toastUtils.registerSuccess()
      
      // Rediriger vers la page de login avec un message
      navigate('/login', { 
        state: { 
          message: 'Inscription réussie ! Veuillez vérifier votre email pour vous connecter.' 
        } 
      })
    } catch (error) {
      console.error("Signup failed:", error)
      toastUtils.validationError(error?.message || tObj.signup.signupError())
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center p-4 pt-24">
      <div className="w-full max-w-5xl">
        <div className="bg-white border border-[#D5DAE3] p-6 sm:p-8 lg:p-12">
          {/* Header */}
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-8 pb-8 border-b border-[#D5DAE3]">
            <div className="flex items-center gap-4">
              <img
                src="/images/logo.png"
                alt="AY Solutions Academy"
                className="h-20 w-auto object-contain"
              />
              <div>
                {enrollmentIntent ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-[#203a8a]">
                        <BookOpen className="w-4 h-4 text-white" />
                      </div>
                      <h1 className="font-display text-2xl lg:text-3xl text-[#203a8a]">
                        {tObj.signup.createAccountToAccessCourse()}
                      </h1>
                    </div>
                    <p className="font-sans text-[#6B7280] text-sm">
                      {tObj.signup.signupNowToStart()}
                    </p>
                    
                    {/* Course Preview Card */}
                    <div className="mt-3 inline-flex items-center gap-3 p-3 bg-[#FAFBFD] border border-[#D5DAE3]">
                      {enrollmentIntent.courseThumbnail && (
                        <img 
                          src={enrollmentIntent.courseThumbnail} 
                          alt={enrollmentIntent.courseTitle}
                          className="w-12 h-12 object-cover"
                        />
                      )}
                      <div>
                        <h3 className="font-semibold text-[#1B2240] text-sm">
                          {enrollmentIntent.courseTitle}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          {enrollmentIntent.isFree ? (
                            <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 bg-[#E8C547] text-[#203a8a] font-semibold">{tObj.signup.free()}</span>
                          ) : (
                            <span className="text-xs font-semibold text-[#1B2240]">
                              {tObj.signup.paidCourse()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className="font-display text-2xl lg:text-3xl text-[#203a8a] mb-2">
                      {tObj.signup.welcomeToAY()}
                    </h1>
                    <p className="font-sans text-[#6B7280] text-sm">{tObj.signup.signup()}</p>
                  </>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="font-sans text-[#6B7280] text-sm">
                {tObj.signup.alreadyHaveAccount()}{" "}
                <a href="/login" className="text-[10px] uppercase tracking-[0.22em] text-[#203a8a] hover:text-[#E8C547] font-semibold transition-colors">
                  {tObj.signup.login()}
                </a>
              </p>
            </div>
          </div>

          {/* Formulaire d'inscription - 2 colonnes */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nom complet - Full width on mobile, 1 col on desktop */}
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">{tObj.signup.fullName()}</label>
                <input
                  type="text"
                  placeholder="Jean Dupont"
                  {...register("name")}
                  maxLength={50}
                  className={`w-full h-12 bg-white border px-4 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] ${
                    errors.name ? "border-red-500" : "border-[#D5DAE3]"
                  }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.name.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">{tObj.signup.emailPlaceholder()}</label>
                <input
                  type="email"
                  placeholder="jean@exemple.com"
                  {...register("email")}
                  className={`w-full h-12 bg-white border px-4 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] ${
                    errors.email ? "border-red-500" : "border-[#D5DAE3]"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.email.message}</p>
                )}
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">{tObj.signup.phoneOptional()}</label>
                <PhoneInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  onCountryChange={setPhoneCountry}
                  defaultCountry="CI"
                  placeholder="+225 07 XX XX XX XX"
                  className="h-12"
                  error={!!errors.phone}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.phone.message}</p>
                )}
              </div>

              {/* Mot de passe */}
              <div className="relative">
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">{tObj.signup.passwordPlaceholder()}</label>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  {...register("password")}
                  className={`w-full h-12 bg-white border px-4 pr-12 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] ${
                    errors.password ? "border-red-500" : "border-[#D5DAE3]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-9 text-[#6B7280] hover:text-[#203a8a] transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.password.message}</p>
                )}
              </div>

              {/* Confirmer mot de passe */}
              <div className="relative">
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">{tObj.signup.confirmPasswordPlaceholder()}</label>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  {...register("confirmPassword")}
                  className={`w-full h-12 bg-white border px-4 pr-12 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] ${
                    errors.confirmPassword ? "border-red-500" : "border-[#D5DAE3]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-9 text-[#6B7280] hover:text-[#203a8a] transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Adresse */}
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">{tObj.signup.addressOptional()}</label>
                <input
                  type="text"
                  placeholder="123 Rue de la République"
                  {...register("address")}
                  className="w-full h-12 bg-white border border-[#D5DAE3] px-4 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a]"
                />
              </div>

              {/* Ville */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">{tObj.signup.cityOptional()}</label>
                <input
                  type="text"
                  placeholder="Abidjan"
                  {...register("city")}
                  className={`w-full h-12 bg-white border px-4 font-sans text-[#1B2240] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-[#203a8a] ${
                    errors.city ? "border-red-500" : "border-[#D5DAE3]"
                  }`}
                />
                {errors.city && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.city.message}</p>
                )}
              </div>

              {/* Pays */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">{tObj.signup.countryOptional()}</label>
                <CountrySelector
                  value={watch("country")}
                  onChange={(countryCode) => setValue("country", countryCode)}
                  placeholder="Côte d'Ivoire"
                  className="h-12"
                  error={!!errors.country}
                />
                {errors.country && (
                  <p className="text-red-500 text-sm mt-1 px-1">{errors.country.message}</p>
                )}
              </div>
            </div>

            {/* Bouton S'inscrire */}
            <div className="pt-6 border-t border-[#D5DAE3]">
              <button
                type="submit"
                disabled={isSubmitting || signupLoading}
                className="w-full h-12 bg-[#203a8a] hover:bg-[#1a2f6e] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
              >
                {isSubmitting || signupLoading ? tObj.signup.signingUp() : tObj.signup.signupButton()}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}